/**
 * Geographic utility functions for distance calculation and city lookup.
 *
 * All distances are in kilometers. Coordinates use standard WGS84
 * (latitude, longitude) format.
 */

/** Default search radius in kilometers */
export const DEFAULT_RADIUS_KM = 25;

/** Available radius options for location-based search (km) */
export const RADIUS_OPTIONS = [5, 10, 25, 50, 100] as const;

/** Convert degrees to radians */
function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Haversine distance between two geographic coordinates.
 * @returns Distance in kilometers
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Baltic city centers with coordinates and country code.
 * Lithuania (LT), Latvia (LV), and Estonia (EE).
 */
export const CITY_CENTERS: Record<
  string,
  { lat: number; lng: number; country: string }
> = {
  // Lithuania
  Vilnius: { lat: 54.6872, lng: 25.2797, country: 'LT' },
  Kaunas: { lat: 54.8985, lng: 23.9036, country: 'LT' },
  Klaipėda: { lat: 55.7033, lng: 21.1443, country: 'LT' },
  Šiauliai: { lat: 55.9349, lng: 23.3137, country: 'LT' },
  Panevėžys: { lat: 55.7348, lng: 24.3575, country: 'LT' },
  Alytus: { lat: 54.3963, lng: 24.0459, country: 'LT' },
  Marijampolė: { lat: 54.5594, lng: 23.35, country: 'LT' },
  Utena: { lat: 55.4981, lng: 25.6032, country: 'LT' },
  Telšiai: { lat: 55.9833, lng: 22.25, country: 'LT' },
  Tauragė: { lat: 55.2513, lng: 22.29, country: 'LT' },

  // Latvia
  Riga: { lat: 56.9496, lng: 24.1052, country: 'LV' },
  Daugavpils: { lat: 55.8747, lng: 26.5361, country: 'LV' },
  Liepāja: { lat: 56.5047, lng: 21.0109, country: 'LV' },
  Jelgava: { lat: 56.6511, lng: 23.7134, country: 'LV' },
  Jūrmala: { lat: 56.968, lng: 23.7704, country: 'LV' },

  // Estonia
  Tallinn: { lat: 59.437, lng: 24.7536, country: 'EE' },
  Tartu: { lat: 58.378, lng: 26.7291, country: 'EE' },
  Narva: { lat: 59.3797, lng: 28.1791, country: 'EE' },
  Pärnu: { lat: 58.3859, lng: 24.4971, country: 'EE' },
};

/**
 * Find the nearest city from CITY_CENTERS to the given coordinates.
 * @returns The closest city name and distance, or null if no cities exist.
 */
export function findNearestCity(
  lat: number,
  lng: number,
): { city: string; distance: number } | null {
  let nearest: { city: string; distance: number } | null = null;

  for (const [name, coords] of Object.entries(CITY_CENTERS)) {
    const distance = haversineDistance(lat, lng, coords.lat, coords.lng);
    if (nearest === null || distance < nearest.distance) {
      nearest = { city: name, distance };
    }
  }

  return nearest;
}

/**
 * Check whether two points are within a given radius of each other.
 */
export function isWithinRadius(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  radiusKm: number,
): boolean {
  return haversineDistance(lat1, lng1, lat2, lng2) <= radiusKm;
}
